import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { readableColor, darken, rgba } from 'polished';

type ButtonProps = React.HTMLAttributes<HTMLButtonElement> & {
    children: React.ReactNode;
    className?: string;
    onClick?: () => void;
    disabled?: boolean;
} & CTAProps;

export interface CTAProps {
    block?: boolean;
    type?: 'submit' | 'reset' | 'button';
    secondary?: boolean;
    small?: boolean;
    dual?: boolean;
    buddies?: boolean;
    jumbo?: boolean;
    cart?: boolean;
    loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props: ButtonProps, ref) => {
    return (
        <Wrapper {...props} className={props.className ?? 'default'} ref={ref}>
            {props.children}
        </Wrapper>
    );
});

interface WrapperProps {
    block?: boolean;
    secondary?: boolean;
    small?: boolean;
    dual?: boolean;
    buddies?: boolean;
    jumbo?: boolean;
    cart?: boolean;
    loading?: boolean;
}

const Wrapper = styled.button<WrapperProps>`
    background: var(--ui_primary_cta);
    transition: background-color 0.2s ease-in-out;
    color: var(--ui_primary_cta_label);

    font-family: var(--text_heading_font);
    letter-spacing: var(--text_heading_font_spacing);
    text-transform: var(--text_heading_font_case);

    font-size: 16px;
    line-height: 16px;

    height: 48px;
    padding: 16px 24px;

    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: auto;

    border: var(--ui_primary_cta_border);
    border-radius: var(--radius_l);

    &.disabled, &[disabled], &.disabled:hover, &[disabled]:hover {
        background: var(--bg-disabled);
        color: var(--text-black);
        border-color: var(--bg-disabled);
        cursor: not-allowed;
    }

    &:hover {
        background: ${(props) => darken(0.05, props.theme.v4.ui_1)};
    }

    &:active {
        background: ${(props) => darken(0.1, props.theme.v4.ui_1)};
    }

    &:focus { outline: none; }

    @media screen and (max-width: 767px) {
        width: 100%;
    }

    ${(props) => props.loading && `
        background: var(--bg-disabled);
        color: var(--text-black);
        border-color: var(--bg-disabled);
        cursor: not-allowed;
    `}

    ${(props) => props.block && `
        width: 100%;
    `}

    ${(props) => props.small && `
        height: 48px;
        padding: 10px 24px;
        font-size: 16px;
        line-height: 20px;
    `}

    ${(props) => props.secondary && `
        background: transparent;
        border: 1px solid #e5e5e5;
        color: #8e8e93;

        &:hover {
            background: var(--bg-grey);
        }
    `}

    ${(props) => props.dual && `
        justify-content: space-between;

        @media screen and (max-width: 767px) {
            padding: 0 16px;
        }
    `}

    ${(props) => props.buddies && `flex: 1 1 0%;`}
`;

export const CTA = styled(Button)``;

export default Button;
